import { useEffect, useRef } from 'react';
import { EditorView } from '@codemirror/view';
import { EditorState } from '@codemirror/state';
import { buildExtensions } from './cmSetup';

interface HtmlEditorProps {
  value: string;
  onChange?: (next: string) => void; // omitted ⇒ read-only
  readOnly?: boolean;
  onFormat?: () => void;
}

export function HtmlEditor({ value, onChange, readOnly, onFormat }: HtmlEditorProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  // Keep latest callbacks reachable from extensions built once at mount.
  const onChangeRef = useRef(onChange);
  const onFormatRef = useRef(onFormat);
  onChangeRef.current = onChange;
  onFormatRef.current = onFormat;

  const ro = !onChange || !!readOnly;

  // Mount / teardown the EditorView once. `ro` change rebuilds it.
  useEffect(() => {
    if (!hostRef.current) return;
    const view = new EditorView({
      parent: hostRef.current,
      state: EditorState.create({
        doc: value,
        extensions: buildExtensions({
          readOnly: ro,
          onChange: ro ? undefined : (next) => onChangeRef.current?.(next),
          onFormat: () => onFormatRef.current?.(),
        }),
      }),
    });
    viewRef.current = view;
    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ro]);

  // Controlled-pattern external-update sync + loop guard.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    if (value === view.state.doc.toString()) return; // typing round-trips here → no-op
    view.dispatch({
      changes: { from: 0, to: view.state.doc.length, insert: value },
    });
  }, [value]);

  return (
    <div
      ref={hostRef}
      className="w-full h-full overflow-hidden"
      onMouseDown={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
    />
  );
}
